/**
 * Balanced SPAKE2 over NIST P-256, per RFC 9382.
 *
 * Ciphersuite: SPAKE2-P256-SHA256-HKDF-SHA256-HMAC-SHA256.
 *   - Group math is provided by the reviewed `@noble/curves/p256`
 *     implementation. No hand-rolled field arithmetic lives here.
 *   - Hash / KDF / MAC reuse the reviewed helpers already vetted in S0
 *     (`sha256`, `hkdfSha256`) plus Node's HMAC-SHA256.
 *
 * Flow (RFC 9382 §3.2):
 *   A (initiator) picks x, sends pA = x*G + w*M.
 *   B (responder) picks y, sends pB = y*G + w*N.
 *   A computes K = x*(pB - w*N);  B computes K = y*(pA - w*M);  both = x*y*G.
 *
 * Key schedule (RFC 9382 §3.3):
 *   TT       = len(A)||A || len(B)||B || len(pA)||pA || len(pB)||pB
 *            || len(K)||K || len(w)||w
 *              (all len() are LITTLE-ENDIAN 8-byte prefixes; the fixed seed
 *               points M and N are NOT in TT — only pA/pB, which embed them)
 *   HASH(TT) = SHA-256(TT)                       (32 bytes)
 *   Ke || Ka = HASH(TT)                          (16 || 16)
 *   KcA||KcB = HKDF-SHA256(Ka, null, "ConfirmationKeys", 32)
 *   cA       = HMAC-SHA256(KcA, TT)
 *   cB       = HMAC-SHA256(KcB, TT)
 *
 * NOTE: this RFC transcript framing (little-endian) is deliberately distinct
 * from the Fume canonical transcript framing (big-endian `uint64be`, see
 * transcript.ts / key-schedule.ts). The two are never mixed.
 */
import { createHmac } from 'node:crypto';
import { p256 } from '@noble/curves/p256';
import { bytesToNumberBE, numberToBytesBE } from '@noble/curves/abstract/utils';
import { sha256 } from './hash.js';
import { hkdfSha256 } from './hkdf.js';
import { FumeCryptoError } from './errors.js';
import { SESSION_ROOT_SIZE, SPAKE2_M_POINT_HEX, SPAKE2_N_POINT_HEX, SPAKE2_CONFIRMATION_INFO, SPAKE2_W_DOMAIN, SPAKE2_DH_INFO, } from './constants.js';
import { bytesEqual, concatBytes, requireLength } from './internal.js';
/** Size, in bytes, of a serialized SPAKE2 scalar (`w`, `x`, `y`). */
export const SPAKE2_SCALAR_SIZE = 32;
/** Size, in bytes, of an uncompressed SEC1 P-256 point. */
const POINT_SIZE = 65;
/** Size, in bytes, of the 128-bit Ke / Ka halves of HASH(TT). */
const HALF_HASH_SIZE = 16;
const utf8 = new TextEncoder();
// ── curve plumbing (reviewed library only) ────────────────────────────────
const Point = p256.ProjectivePoint;
const ORDER = p256.CURVE.n;
const M_POINT = Point.fromHex(SPAKE2_M_POINT_HEX);
const N_POINT = Point.fromHex(SPAKE2_N_POINT_HEX);
/** Reduce a big-endian byte string modulo the group order; reject a 0 result. */
function scalarFromBytes(bytes, name) {
    const s = bytesToNumberBE(bytes) % ORDER;
    if (s === 0n) {
        throw new FumeCryptoError(`${name} reduces to zero modulo the group order`);
    }
    return s;
}
/** Fixed-length 32-byte big-endian encoding of a scalar in [1, n). */
function scalarToBytes(s) {
    return numberToBytesBE(s, SPAKE2_SCALAR_SIZE);
}
/** Decode + validate a 65-byte uncompressed P-256 point from the wire. */
function decodePoint(bytes, name) {
    if (!(bytes instanceof Uint8Array) || bytes.length !== POINT_SIZE) {
        throw new FumeCryptoError(`${name} must be a ${POINT_SIZE}-byte uncompressed P-256 point`);
    }
    let point;
    try {
        point = Point.fromHex(bytes); // validates on-curve membership
        point.assertValidity();
    }
    catch {
        throw new FumeCryptoError(`${name} is not a valid P-256 point`);
    }
    if (point.equals(Point.ZERO)) {
        throw new FumeCryptoError(`${name} must not be the identity element`);
    }
    return point;
}
// ── deterministic-ephemeral test hook (KAT / determinism vectors) ──────────
let injectedEphemeral = null;
/**
 * TEST-ONLY: pin the next ephemeral scalar consumed by `spake2Initiate` or
 * `spake2Respond` (RFC 9382 KAT `x` / `y` injection). One-shot: it is cleared
 * once consumed. Not re-exported from the package index.
 */
export function _setNextEphemeralScalar(scalar) {
    injectedEphemeral = scalar === null ? null : Uint8Array.from(scalar);
}
function nextEphemeralScalar() {
    if (injectedEphemeral !== null) {
        const bytes = injectedEphemeral;
        injectedEphemeral = null;
        return scalarFromBytes(bytes, 'ephemeral scalar');
    }
    // Reviewed uniform scalar in [1, n) from the curve library's CSPRNG helper.
    return scalarFromBytes(p256.utils.randomPrivateKey(), 'ephemeral scalar');
}
function isInitiatorState(state) {
    return (typeof state === 'object' &&
        state !== null &&
        state.__brand === 'fume.spake2.initiator' &&
        state.pA instanceof Uint8Array);
}
// ── scalar derivation ───────────────────────────────────────────────────────
/**
 * Derive the SPAKE2 password scalar `w` from the 32-byte session root `R`.
 *   w = red_n( SHA-256("fume.spake2.w.v1" || R) )
 * `R` is high-entropy (>=128 bits), so modular-reduction bias is negligible.
 * Returned as a fixed-length 32-byte big-endian scalar in [1, n).
 */
export function deriveSpake2Scalar(R) {
    requireLength(R, SESSION_ROOT_SIZE, 'R');
    const digest = sha256(concatBytes(utf8.encode(SPAKE2_W_DOMAIN), R));
    return scalarToBytes(scalarFromBytes(digest, 'w'));
}
// ── RFC 9382 transcript + key schedule ──────────────────────────────────────
/** 8-byte little-endian length prefix, per RFC 9382 §3.3. */
function lenLE(n) {
    const out = new Uint8Array(8);
    new DataView(out.buffer).setBigUint64(0, BigInt(n), true /* little-endian */);
    return out;
}
function frameLE(field) {
    return concatBytes(lenLE(field.length), field);
}
/**
 * Assemble the RFC 9382 transcript TT from the exchanged public values.
 *   TT = len(A)||A || len(B)||B || len(pA)||pA || len(pB)||pB || len(K)||K || len(w)||w
 * Per RFC 9382 §3.3 the fixed seed points M and N are NOT part of TT (they are
 * public constants); they only shape pA/pB, which ARE committed here.
 */
function assembleTT(identityA, identityB, pA, pB, K, w) {
    return concatBytes(frameLE(utf8.encode(identityA)), frameLE(utf8.encode(identityB)), frameLE(pA), frameLE(pB), frameLE(K), frameLE(w));
}
/** Split HASH(TT) into (Ke, Ka) and package the shared secret. */
function sharedSecretFromTT(tt) {
    const hash = sha256(tt);
    return {
        ke: hash.slice(0, HALF_HASH_SIZE),
        ka: hash.slice(HALF_HASH_SIZE),
        transcript: tt,
    };
}
/** Derive RFC 9382 confirmation keys: KcA || KcB = HKDF-SHA256(Ka, null, "ConfirmationKeys", 32). */
export function deriveConfirmationKeys(ka) {
    requireLength(ka, HALF_HASH_SIZE, 'ka');
    const okm = hkdfSha256(ka, null, utf8.encode(SPAKE2_CONFIRMATION_INFO), 2 * HALF_HASH_SIZE);
    return { kcA: okm.slice(0, HALF_HASH_SIZE), kcB: okm.slice(HALF_HASH_SIZE) };
}
/** c = HMAC-SHA256(Kc, transcript) where `transcript` is the RFC 9382 TT. */
export function computeConfirmationTag(kc, transcript) {
    if (!(kc instanceof Uint8Array) || kc.length === 0) {
        throw new FumeCryptoError('confirmation key must be a non-empty Uint8Array');
    }
    if (!(transcript instanceof Uint8Array)) {
        throw new FumeCryptoError('transcript must be a Uint8Array');
    }
    return new Uint8Array(createHmac('sha256', kc).update(transcript).digest());
}
/**
 * Verify a confirmation tag in constant time. Returns `false` (never throws)
 * for any mismatch — tampered tag, tampered transcript, or swapped key.
 */
export function verifyConfirmationTag(kc, transcript, tag) {
    if (!(tag instanceof Uint8Array))
        return false;
    let expected;
    try {
        expected = computeConfirmationTag(kc, transcript);
    }
    catch {
        return false;
    }
    // bytesEqual short-circuits only on length; byte comparison is constant time.
    return bytesEqual(expected, tag);
}
// ── two-message flow ─────────────────────────────────────────────────────────
function validateContext(context) {
    if (context == null ||
        typeof context.identityA !== 'string' ||
        typeof context.identityB !== 'string') {
        throw new FumeCryptoError('context must provide string identityA and identityB');
    }
}
/**
 * Initiator (A) first pass. Generates ephemeral `x`, returns pA = x*G + w*M and
 * opaque state. `password` is the 32-byte SPAKE2 scalar `w`.
 */
export function spake2Initiate(password, context) {
    requireLength(password, SPAKE2_SCALAR_SIZE, 'password');
    validateContext(context);
    const w = scalarFromBytes(password, 'w');
    const x = nextEphemeralScalar();
    const pA = Point.BASE.multiply(x).add(M_POINT.multiply(w)).toRawBytes(false);
    const mask = p256.utils.randomPrivateKey(); // 32 random bytes for blinding
    const wBytes = scalarToBytes(w);
    const wMasked = new Uint8Array(SPAKE2_SCALAR_SIZE);
    for (let i = 0; i < SPAKE2_SCALAR_SIZE; i++)
        wMasked[i] = wBytes[i] ^ mask[i];
    const state = {
        __brand: 'fume.spake2.initiator',
        identityA: context.identityA,
        identityB: context.identityB,
        x: scalarToBytes(x),
        wMasked,
        mask,
        pA,
    };
    return { message: pA, state };
}
/**
 * Responder (B) pass. Validates pA, generates ephemeral `y`, derives the shared
 * secret K = y*(pA - w*M), and produces cB plus the expected cA.
 */
export function spake2Respond(password, initiatorMessage, context) {
    requireLength(password, SPAKE2_SCALAR_SIZE, 'password');
    validateContext(context);
    const w = scalarFromBytes(password, 'w');
    const pAPoint = decodePoint(initiatorMessage, 'initiatorMessage');
    const y = nextEphemeralScalar();
    const pB = Point.BASE.multiply(y).add(N_POINT.multiply(w)).toRawBytes(false);
    // K = y * (pA - w*M)
    const K = pAPoint.subtract(M_POINT.multiply(w)).multiply(y).toRawBytes(false);
    const tt = assembleTT(context.identityA, context.identityB, initiatorMessage, pB, K, scalarToBytes(w));
    const sharedSecret = sharedSecretFromTT(tt);
    const keys = deriveConfirmationKeys(sharedSecret.ka);
    return {
        message: pB,
        sharedSecret,
        confirmationLocal: computeConfirmationTag(keys.kcB, tt),
        confirmationRemoteExpected: computeConfirmationTag(keys.kcA, tt),
    };
}
/**
 * Initiator (A) finish. Validates pB, derives K = x*(pB - w*N), and produces cA
 * plus the expected cB.
 */
export function spake2Finish(state, responderMessage, context) {
    if (!isInitiatorState(state)) {
        throw new FumeCryptoError('invalid SPAKE2 initiator state');
    }
    validateContext(context);
    const pBPoint = decodePoint(responderMessage, 'responderMessage');
    // Unblind w transiently (never persisted in cleartext).
    const wBytes = new Uint8Array(SPAKE2_SCALAR_SIZE);
    for (let i = 0; i < SPAKE2_SCALAR_SIZE; i++)
        wBytes[i] = state.wMasked[i] ^ state.mask[i];
    const w = scalarFromBytes(wBytes, 'w');
    const x = scalarFromBytes(state.x, 'x');
    // K = x * (pB - w*N)
    const K = pBPoint.subtract(N_POINT.multiply(w)).multiply(x).toRawBytes(false);
    const tt = assembleTT(context.identityA, context.identityB, state.pA, responderMessage, K, wBytes);
    const sharedSecret = sharedSecretFromTT(tt);
    const keys = deriveConfirmationKeys(sharedSecret.ka);
    return {
        sharedSecret,
        confirmationLocal: computeConfirmationTag(keys.kcA, tt),
        confirmationRemoteExpected: computeConfirmationTag(keys.kcB, tt),
    };
}
// ── SPAKE2 Ke → Fume dh ──────────────────────────────────────────────────────
/**
 * Expand SPAKE2 `Ke` into the 32-byte `dh` value consumed by `deriveKeys()`.
 *   dh = HKDF-SHA256(salt = null, ikm = ke, info = "fume.spake2.dh.v1", L = 32)
 */
export function deriveDhFromKe(ke) {
    requireLength(ke, HALF_HASH_SIZE, 'ke');
    return hkdfSha256(ke, null, utf8.encode(SPAKE2_DH_INFO), SESSION_ROOT_SIZE);
}
//# sourceMappingURL=pake.js.map